import { useEffect, useState } from 'react';
import { comandasService } from '../services/api';

interface Props {
  cuenta: any;
  onClose: () => void;
  /** El reparto cambió (se dividió, se movieron productos o se deshizo). */
  onActualizada: () => void;
}

/* Dividir una mesa NO parte la comanda: crea sub-mesas.
   Mesa 3 dividida en 2 → "Mesa 3" (lo que no se movió) y "Mesa 3.1".
   Cada una se cobra por separado desde la lista de la caja, en el orden que
   quiera el cajero. Aquí solo se reparten los productos. */
export default function ModalSplitCuentas({ cuenta, onClose, onActualizada }: Props) {
  const mesa = cuenta.mesa_numero;
  const cuentas: any[] = cuenta.cuentas || [];
  const yaDividida = cuentas.length > 1;

  const [partes, setPartes] = useState(2);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState<string | null>(null);
  const [partesDiv, setPartesDiv] = useState<Record<string, string>>({});
  const [dividiendo, setDividiendo] = useState<string | null>(null);

  // Asignación local para que la UI responda al instante
  const [asig, setAsig] = useState<Record<string, string>>({});
  useEffect(() => {
    const inicial: Record<string, string> = {};
    for (const i of cuenta.items || []) {
      if (i.id) inicial[i.id] = i.subcuenta_id || 'base';
    }
    setAsig(inicial);
  }, [cuenta]);

  const dividirMesa = async () => {
    setCreando(true); setError('');
    try {
      await comandasService.crearSubcuentas(cuenta.comanda_id, partes);
      onActualizada();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No se pudo dividir la cuenta');
    } finally { setCreando(false); }
  };

  const deshacer = async () => {
    if (!window.confirm(`¿Volver a juntar todo en la Mesa ${mesa}?`)) return;
    setError('');
    try {
      await comandasService.borrarSubcuentas(cuenta.comanda_id);
      onActualizada();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No se pudo deshacer la división');
    }
  };

  const mover = async (item_id: string, destino: string) => {
    const previo = asig[item_id];
    setAsig(p => ({ ...p, [item_id]: destino }));
    setGuardando(item_id);
    setError('');
    try {
      await comandasService.moverItemSubcuenta(
        cuenta.comanda_id, item_id, destino === 'base' ? null : destino
      );
      onActualizada();
    } catch (e: any) {
      setAsig(p => ({ ...p, [item_id]: previo }));
      setError(e?.response?.data?.error || 'No se pudo mover el producto');
    } finally { setGuardando(null); }
  };

  const dividirProducto = async (item_id: string) => {
    const n = parseInt(partesDiv[item_id] || '');
    if (!n || n < 2) return;
    setDividiendo(item_id); setError('');
    try {
      await comandasService.dividirItem(cuenta.comanda_id, item_id, n);
      onActualizada();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No se pudo dividir el producto');
    } finally { setDividiendo(null); }
  };

  const items = (cuenta.items || []).filter((i: any) => i.id);
  const itemsDe = (id: string) => items.filter((i: any) => (i.subcuenta_id || 'base') === id);
  const totalDe = (id: string) =>
    itemsDe(id).reduce((s: number, i: any) => s + (i.importe || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Dividir cuenta · Mesa {mesa}</h3>
            <p className="text-xs text-gray-500">
              Cada cuenta se cobra por separado desde la lista de la caja
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="p-6">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-2 mb-4">{error}</div>}

          {!yaDividida ? (
            <div>
              <p className="text-sm text-gray-500 mb-1">¿En cuántas cuentas se divide la mesa?</p>
              <p className="text-xs text-gray-400 mb-4">
                La primera conserva el número de la mesa y las demás quedan como
                Mesa {mesa}.1, Mesa {mesa}.2…
              </p>
              <div className="flex gap-2 mb-6">
                {[2,3,4,5,6].map(n => (
                  <button
                    key={n}
                    onClick={() => setPartes(n)}
                    className={`w-11 h-11 rounded-full text-sm font-bold border-2 transition-all ${partes === n ? 'border-[var(--brand-texto)] bg-[var(--brand)] text-[var(--on-brand)]' : 'border-gray-200 text-gray-500 hover:border-[var(--brand-texto)]'}`}
                  >{n}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {Array.from({ length: partes }, (_, i) => (
                  <span key={i} className="px-3 py-1 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold">
                    Mesa {i === 0 ? mesa : `${mesa}.${i}`}
                  </span>
                ))}
              </div>
              <button
                onClick={dividirMesa}
                disabled={creando}
                className="w-full bg-[var(--brand)] text-[var(--on-brand)] py-3 rounded-xl font-semibold disabled:opacity-40"
              >
                {creando ? 'Dividiendo…' : `Dividir en ${partes} cuentas`}
              </button>
            </div>
          ) : (
            <div>
              {/* Resumen de cuentas */}
              <div className="flex flex-wrap gap-2 mb-5">
                {cuentas.map(c => (
                  <div
                    key={c.subcuenta_id}
                    className={`px-4 py-2 rounded-xl border-2 ${c.pagada ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}
                  >
                    <span className="block text-sm font-bold text-gray-800">
                      {c.pagada ? '✅ ' : ''}Mesa {c.etiqueta}
                    </span>
                    <span className="block text-xs text-gray-500">
                      ${totalDe(c.subcuenta_id).toFixed(2)} · {itemsDe(c.subcuenta_id).length} prod.
                    </span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-gray-500 mb-3">
                Toca el destino de cada producto. Si un platillo se comparte, pártelo primero.
              </p>

              <div className="space-y-2 mb-6">
                {items.map((item: any) => (
                  <div key={item.id} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-800 text-sm">
                          {item.cantidad}x {item.producto}
                        </span>
                        <span className="block text-xs text-gray-400">
                          ${item.importe?.toFixed(2)}
                          {cuenta.folios_count > 1 && ` · ronda ${item.folio_num}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <input
                          type="number" min="2" max="10"
                          className="w-14 border border-gray-200 rounded-lg px-2 py-1 text-xs"
                          placeholder="partes"
                          value={partesDiv[item.id] || ''}
                          onChange={e => setPartesDiv(p => ({ ...p, [item.id]: e.target.value }))}
                        />
                        <button
                          onClick={() => dividirProducto(item.id)}
                          disabled={!partesDiv[item.id] || dividiendo === item.id}
                          className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 px-2 py-1 rounded-lg disabled:opacity-40 font-medium"
                          title="Partir el precio de este producto entre varias cuentas"
                        >
                          {dividiendo === item.id ? '…' : '÷ Partir'}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {cuentas.map(c => {
                        const activa = (asig[item.id] || 'base') === c.subcuenta_id;
                        return (
                          <button
                            key={c.subcuenta_id}
                            onClick={() => mover(item.id, c.subcuenta_id)}
                            disabled={c.pagada || guardando === item.id}
                            title={c.pagada ? 'Esta cuenta ya se cobró' : undefined}
                            className={`text-xs px-3 py-1 rounded-lg border font-medium transition-all disabled:opacity-40 ${activa ? 'border-[var(--brand-texto)] bg-[var(--brand)] text-[var(--on-brand)]' : 'border-gray-200 text-gray-600 hover:border-[var(--brand-texto)]'}`}
                          >
                            Mesa {c.etiqueta}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={deshacer}
                  className="flex-1 border border-gray-200 text-gray-500 py-3 rounded-xl font-medium"
                >
                  Deshacer división
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 bg-[var(--brand)] text-[var(--on-brand)] py-3 rounded-xl font-semibold"
                >
                  Listo — ir a cobrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
